/**
 * Setting up a phone APP from a QR code (ArcSIP).
 *
 * The portal shows an extension's QR code under Provisioning & mobile; the app
 * scans it and fills in the account. The code carries a link:
 *
 *   arcsip://provision?v=1&ext=1003&user=1003&pass=...&domain=meridian.example
 *     &host=meridian.example&port=5060&transport=udp&name=Priya%20Naidoo
 *     &codecs=PCMA,PCMU,OPUS,G722
 *
 * WHY A LINK WITH PLAIN PARAMETERS
 *
 * A link, so a phone's own camera can hand it straight to the app once the app
 * registers the scheme. Plain query parameters rather than an encoded blob,
 * because every platform the app might be built on parses a query string
 * natively, and a person can read one when a scan goes wrong.
 *
 * VERSIONING. `v` is the format version. Within a version, parameters are only
 * ever ADDED -- an app ignores any it does not know, and treats every one after
 * the required set as optional. Changing the meaning of one is a new `v`.
 *
 *   required  v, user, pass, domain, host
 *   optional  ext (dial number; defaults to user), name (display name),
 *             port (default 5060), transport (udp|tcp|tls, default udp),
 *             codecs (comma list, most preferred first)
 *
 * The QR code carries the SIP PASSWORD. The portal shows it only behind the
 * same step-up check and audit as revealing the password, and clears it after
 * two minutes. Anyone who photographs it can register as this extension until
 * the password is rotated.
 */
import { z } from 'zod';
import { CodecSchema } from './telephony.js';
import { IdSchema, TimestampSchema } from './common.js';
import {
  DeviceNetworkSchema, DeviceVendorSchema, MacAddressSchema, ProvisioningRoleSchema,
} from './devices.js';

export const PROVISIONING_SCHEME = 'arcsip';
export const PROVISIONING_VERSION = 1;

/**
 * What a handset WOULD be served, for staff to read.
 *
 * Generated by the same code that answers the phone, so a preview cannot drift
 * from what the phone gets. The password is redacted unless it was asked for
 * explicitly, which needs the step-up permission for revealing SIP passwords
 * and is audited separately (API modules/provisioning/admin.ts).
 */
export const ProvisioningPreviewSchema = z.object({
  vendor: z.string(),
  vendor_label: z.string(),
  /**
   * False when this make has no generator yet: the file is then a listing of
   * the settings to enter by hand, not a configuration the phone can read.
   */
  generates_config: z.boolean(),
  /** Lines left out because no recoverable password exists for them. */
  skipped_lines: z.number().int(),
  /** The template its keys come from, or null for a handset still laid out by its role. */
  template: z.object({
    id: z.string(), slug: z.string(), name: z.string(), version: z.number().int(),
  }).nullable(),
  /** Keys its template asks for that this phone cannot have, in words. */
  warnings: z.array(z.string()),
  files: z.array(z.object({
    name: z.string(),
    content_type: z.string(),
    body: z.string(),
  })),
  password_shown: z.boolean(),
});
export type ProvisioningPreview = z.infer<typeof ProvisioningPreviewSchema>;

/** Which makes of phone the platform can generate a configuration for. */
export const ProvisioningVendorSchema = z.object({
  vendor: z.string(),
  label: z.string(),
  generates_config: z.boolean(),
});
export type ProvisioningVendor = z.infer<typeof ProvisioningVendorSchema>;

export const MobileProvisioningSchema = z.object({
  v: z.literal(1),
  /** What people dial to reach the seat, for display. Never the login. */
  ext: z.string().min(1).max(64),
  /** The SIP username to register and authenticate with, e.g. 1001-meridian (docs/26 E8). */
  user: z.string().min(1).max(128),
  pass: z.string().min(1).max(256),
  /** The SIP domain -- the digest realm and the right-hand side of the address. */
  domain: z.string().min(1).max(253),
  /** Where to register. The same as the domain here; kept apart so it need not be. */
  host: z.string().min(1).max(253),
  port: z.number().int().min(1).max(65535).default(5060),
  transport: z.enum(['udp', 'tcp', 'tls']).default('udp'),
  name: z.string().max(128).nullable().optional(),
  codecs: z.array(CodecSchema).min(1).max(4).optional(),
});
export type MobileProvisioning = z.infer<typeof MobileProvisioningSchema>;

/** The link the QR code carries. Parameter order is fixed so the code is stable. */
export function encodeProvisioningUri(p: MobileProvisioning): string {
  const q: Array<[string, string]> = [
    ['v', String(p.v)], ['ext', p.ext], ['user', p.user], ['pass', p.pass],
    ['domain', p.domain], ['host', p.host], ['port', String(p.port)], ['transport', p.transport],
  ];
  if (p.name) q.push(['name', p.name]);
  if (p.codecs?.length) q.push(['codecs', p.codecs.join(',')]);
  // encodeURIComponent, not URLSearchParams: a space must be %20, because
  // several mobile URL parsers do not read '+' as a space.
  return `${PROVISIONING_SCHEME}://provision?${q.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&')}`;
}

/**
 * The reverse, for tests and for any client written in TypeScript. Throws on a
 * link that is not a version this code understands.
 */
export function decodeProvisioningUri(uri: string): MobileProvisioning {
  const m = /^arcsip:\/\/provision\?(.*)$/.exec(uri.trim());
  if (!m) throw new Error('not an ArcSIP provisioning link');
  const params = new Map<string, string>();
  for (const part of m[1]!.split('&')) {
    if (!part) continue;
    const i = part.indexOf('=');
    const k = decodeURIComponent(i < 0 ? part : part.slice(0, i));
    params.set(k, decodeURIComponent(i < 0 ? '' : part.slice(i + 1)));
  }
  const codecs = params.get('codecs');
  return MobileProvisioningSchema.parse({
    v: Number(params.get('v')),
    ext: params.get('ext') ?? params.get('user'),
    user: params.get('user'),
    pass: params.get('pass'),
    domain: params.get('domain'),
    host: params.get('host'),
    port: params.has('port') ? Number(params.get('port')) : undefined,
    transport: params.get('transport') ?? undefined,
    name: params.get('name') ?? null,
    codecs: codecs ? codecs.split(',').filter(Boolean) : undefined,
  });
}

/** A provisioning role as the portal offers it, read from the module's own templates. */
export const ProvisioningRoleInfoSchema = z.object({
  role: ProvisioningRoleSchema,
  name: z.string(),
  description: z.string(),
  needs_xml_browser: z.boolean(),
});
export type ProvisioningRoleInfo = z.infer<typeof ProvisioningRoleInfoSchema>;

/** One phone in the supported-handset catalogue (docs/26 C1). */
export const HandsetModelSchema = z.object({
  id: z.string(),
  vendor: DeviceVendorSchema,
  vendor_label: z.string(),
  model: z.string(),
  max_lines: z.number().int(),
  line_keys: z.number().int().nullable(),
  xml_browser: z.boolean(),
  active: z.boolean(),
  notes: z.string().nullable(),
  /** Whether the platform writes this make's configuration, or lists settings to type in. */
  generates_config: z.boolean(),
  /** Whether this make's plug-in has the hot desk login screens. */
  desk_pages: z.boolean(),
  /** Handsets on this model that the caller can see. */
  device_count: z.number().int(),
});
export type HandsetModel = z.infer<typeof HandsetModelSchema>;

export const HandsetModelCreateSchema = z.object({
  vendor: DeviceVendorSchema,
  model: z.string().trim().min(1).max(40),
  max_lines: z.number().int().min(1).max(24),
  line_keys: z.number().int().min(0).max(120).nullable().default(null),
  xml_browser: z.boolean().default(false),
  active: z.boolean().default(true),
  notes: z.string().trim().max(500).nullable().default(null),
}).strict();

/** Vendor and model are what an entry IS, so they are not edited: add another. */
export const HandsetModelUpdateSchema = HandsetModelCreateSchema
  .omit({ vendor: true, model: true }).partial().strict();

// ------------------------------------------------------------------ templates

/**
 * Provisioning templates (docs/29): one handset model, its key layout and shared network
 * settings. On an extension somebody enters the phone's MAC and chooses a template; hundreds
 * of phones can share one, and one extension can have its own copy.
 *
 * A template positions keys and never decides what a phone registers as. Which extensions sit
 * on a phone's accounts is chosen on the phone, so editing a template many phones share
 * cannot take one of them off the network.
 */

/**
 * What a key does, as stored.
 *
 * line         one of the phone's own accounts (account)
 * blf          a busy lamp for a named extension; account templates only
 * blf_next     a lamp for the next colleague in number order, so a platform template can give
 *              a reception phone lamps without naming anybody
 * speed_dial   a number (value)
 * feature      one of the account's feature codes (value), dialled with the account's own code
 * desk_login   the hot desk log in page, or its feature code where the phone has no browser
 * desk_logout  the log out page, likewise
 */
export const TEMPLATE_KEY_KINDS = [
  'line', 'blf', 'blf_next', 'speed_dial', 'feature', 'desk_login', 'desk_logout',
] as const;
export const TemplateKeyKindSchema = z.enum(TEMPLATE_KEY_KINDS);
export type TemplateKeyKind = z.infer<typeof TemplateKeyKindSchema>;

/** The feature codes a key may dial. */
export const TEMPLATE_KEY_FEATURES = [
  'voicemail_own', 'dnd_on', 'dnd_off', 'fwd_off', 'queue_pause', 'queue_unpause',
] as const;
export type TemplateKeyFeature = (typeof TEMPLATE_KEY_FEATURES)[number];

/** What a new template starts as: blank, or one of the four layouts handsets had. */
export const TEMPLATE_STARTS = ['blank', 'back_office', 'receptionist', 'agent', 'hot_desking'] as const;
export type TemplateStart = (typeof TEMPLATE_STARTS)[number];

const ONE_LINE = /^[^\r\n]*$/;
const TEMPLATE_NAME = z.string().trim().min(1).max(60).regex(ONE_LINE, 'must be one line');
const HANDSET_MODEL_ID = z.string().regex(/^[a-z0-9][a-z0-9-]{1,40}$/);

export const TemplateKeyInputSchema = z.object({
  position: z.number().int().min(1).max(120),
  kind: TemplateKeyKindSchema,
  account: z.number().int().min(1).max(24).nullable().default(null),
  extension_id: IdSchema.nullable().default(null),
  value: z.string().trim().max(32).nullable().default(null),
  /** Null shows what the key does: the line's name, the colleague's, the feature's. */
  label: z.string().trim().max(32).regex(ONE_LINE, 'must be one line').nullable().default(null),
}).strict().superRefine((k, ctx) => {
  const need = (field: 'account' | 'extension_id' | 'value', message: string) =>
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });
  if (k.kind === 'line' && k.account === null) need('account', 'choose which account the key is');
  if (k.kind === 'blf' && k.extension_id === null) need('extension_id', 'choose whose lamp it is');
  if (k.kind === 'speed_dial' && !/^[0-9*#+]{1,32}$/.test(k.value ?? '')) {
    need('value', 'digits, *, # and + only');
  }
  if (k.kind === 'feature' && !(TEMPLATE_KEY_FEATURES as readonly string[]).includes(k.value ?? '')) {
    need('value', 'choose a feature');
  }
});
export type TemplateKeyInput = z.infer<typeof TemplateKeyInputSchema>;

export const TemplateKeySchema = z.object({
  position: z.number().int(),
  kind: TemplateKeyKindSchema,
  account: z.number().int().nullable(),
  extension_id: IdSchema.nullable(),
  /** The named extension as it is now: a renumbered colleague keeps their lamp. */
  extension_number: z.string().nullable(),
  extension_name: z.string().nullable(),
  value: z.string().nullable(),
  label: z.string().nullable(),
});
export type TemplateKey = z.infer<typeof TemplateKeySchema>;

export const ProvisioningTemplateSchema = z.object({
  id: IdSchema,
  /** Null for a platform template, which every account can use. */
  tenant_id: IdSchema.nullable(),
  /** Set for a personal template: one extension's own copy. */
  extension_id: IdSchema.nullable(),
  extension_number: z.string().nullable(),
  slug: z.string(),
  name: z.string(),
  handset_model_id: z.string(),
  vendor: DeviceVendorSchema,
  vendor_label: z.string(),
  model: z.string(),
  /** Programmable keys the model has, when the catalogue records it. */
  line_keys: z.number().int().nullable(),
  max_lines: z.number().int(),
  network_mode: z.enum(['unmanaged', 'dhcp']),
  vlan_id: z.number().int().nullable(),
  notes: z.string().nullable(),
  version: z.number().int(),
  /** Phones using it that the caller can see. */
  phone_count: z.number().int(),
  keys: z.array(TemplateKeySchema),
  updated_at: TimestampSchema,
});
export type ProvisioningTemplate = z.infer<typeof ProvisioningTemplateSchema>;

export const ProvisioningTemplateCreateSchema = z.object({
  name: TEMPLATE_NAME,
  handset_model_id: HANDSET_MODEL_ID,
  start_from: z.enum(TEMPLATE_STARTS).default('back_office'),
}).strict();
export type ProvisioningTemplateCreate = z.infer<typeof ProvisioningTemplateCreateSchema>;

export const ProvisioningTemplateUpdateSchema = z.object({
  /** The version the edit started from: saving over somebody else's change is refused. */
  version: z.number().int().min(1),
  /** How many phones the editor said it reaches: refused if that has moved since. */
  expected_phone_count: z.number().int().min(0),
  /** Required to change network settings on a template phones are using. */
  confirm_network: z.boolean().default(false),
  name: TEMPLATE_NAME,
  notes: z.string().trim().max(500).nullable().default(null),
  network_mode: z.enum(['unmanaged', 'dhcp']),
  vlan_id: z.number().int().min(1).max(4094).nullable().default(null),
  keys: z.array(TemplateKeyInputSchema).max(120),
}).strict().superRefine((t, ctx) => {
  const seen = new Set<number>();
  t.keys.forEach((k, i) => {
    if (seen.has(k.position)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['keys', i, 'position'], message: 'two keys in one place' });
    }
    seen.add(k.position);
  });
});
export type ProvisioningTemplateUpdate = z.infer<typeof ProvisioningTemplateUpdateSchema>;

export const ProvisioningTemplateDuplicateSchema = z.object({
  name: TEMPLATE_NAME,
  /** Another model: keys past its last key are dropped, and listed. */
  handset_model_id: HANDSET_MODEL_ID.optional(),
  /** A personal copy for this extension ("Change keys for 1001"). */
  extension_id: IdSchema.optional(),
}).strict();
export type ProvisioningTemplateDuplicate = z.infer<typeof ProvisioningTemplateDuplicateSchema>;

/** A desk phone added on an extension: its MAC and its template. */
export const ExtensionPhoneCreateSchema = z.object({
  mac_address: MacAddressSchema,
  template_id: IdSchema,
  label: z.string().trim().max(120).nullable().default(null),
  /** The phone's own static address and VLAN, which no template can share. */
  network: DeviceNetworkSchema.optional(),
  /** Other extensions on accounts 2 and up. Account 1 is the extension itself. */
  extra_lines: z.array(z.object({
    extension_id: IdSchema,
    line_number: z.number().int().min(2).max(24),
    label: z.string().max(64).optional(),
  })).max(23).default([]),
}).strict();
export type ExtensionPhoneCreate = z.infer<typeof ExtensionPhoneCreateSchema>;

/** Moves a phone to another extension on the same account, keeping its identity. */
export const DeviceMoveSchema = z.object({ extension_id: IdSchema }).strict();

/** One template for many phones at once. */
export const AssignTemplateSchema = z.object({
  device_ids: z.array(IdSchema).min(1).max(500),
  template_id: IdSchema,
}).strict();
